/**
 * Mirrors android/…/Navigation/ParentRoute.kt.
 *
 * The route strings are the same on all three clients, which on the web means they are the URLs. That is
 * divergence 13 in the parity register: the apps model navigation as exclusive shells with no address bar,
 * and the web has to reconcile that with one.
 *
 * **The child-mode boundary is unchanged — a URL cannot cross it.** `/child/*` renders only while a child
 * token exists and `/parent/*` only while one does not, so typing the other path does not get you there.
 * That is the same guarantee ADR-0006 makes on the apps, enforced at the shell rather than by hiding a
 * link, and the API refuses the crossing regardless.
 */
export const ParentRoute = {
  children: '/parent/children',
  overview: '/parent/overview',
  settings: '/parent/settings',
  addChild: '/parent/children/new',
  dashboard: (childId: string): string => `/parent/children/${childId}`,
  skill: (childId: string, skillCode: string): string => `/parent/children/${childId}/${skillCode}`,
  achievements: (childId: string): string => `/parent/children/${childId}/achievements`,
} as const;

export const ChildRoute = {
  home: '/child/home',
  session: '/child/session',
  island: '/child/island',
  achievements: '/child/achievements',
} as const;

export const PublicRoute = {
  welcome: '/',
  signIn: '/sign-in',
  register: '/register',
  forgotPassword: '/forgot-password',
  /**
   * The address a reset mail points at — `AUTH_PASSWORD_RESET_URL` on the API must agree with this.
   * The two apps open it in a browser rather than growing a screen of their own (R0040).
   */
  resetPassword: '/reset-password',
  /**
   * Where a provider sign-in comes back to. Must be on the API's `OAUTH_REDIRECT_URLS` allow-list, or the
   * callback answers with JSON instead of returning here (R0148).
   */
  oauthCallback: '/auth/callback',
  /**
   * Where a verification link points — `AUTH_EMAIL_VERIFICATION_URL` on the API must agree with this.
   * Public, because a parent may open the mail on a device they are not signed in on (R0066).
   */
  verifyEmail: '/verify-email',
} as const;
